import ExcelJS from 'exceljs';
import { InvoiceItem, defaultInvoiceItem } from '../config/types';
import { createExcelFromTemplate } from './excelService';
import { toDate } from '../utils/dates';
import { matchesDestination } from '../utils/destinations';

/**
 * Read-only export of saved loads into the same Loads.xlsx sheet the Data Entry
 * "Generate Excel" button produces. Nothing here writes to Firestore.
 */

export const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];

export interface ArchiveCriteria {
  /** Four-digit year, or 'all' */
  year: number | 'all';
  /** 0-11, or 'all' */
  month: number | 'all';
  /** Partial destination match; empty means any */
  destination: string;
}

export const ALL_TIME: ArchiveCriteria = { year: 'all', month: 'all', destination: '' };

// The sheet's own status column sits just past the template's last column (AC).
const STATUS_COLUMN = 30; // AD
const RECORDED_TOTAL_COLUMN = 31; // AE
const HEADER_ROW = 3;
const FIRST_DATA_ROW = 4;

const STATUS_TEXT: Record<string, string> = {
  pending: 'Pending',
  pay_to_driver: 'Pay to Driver',
  PAID: 'Paid'
};

/**
 * The day a load belongs to: its load date when it has one (a plain
 * YYYY-MM-DD, read as a local date so it never slips a day), else when it was
 * entered.
 */
export const loadDate = (invoice: any): Date | null => {
  const raw = typeof invoice.date === 'string' ? invoice.date.trim() : '';
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return toDate(invoice.createdAt);
};

export const matchesArchiveCriteria = (invoice: any, criteria: ArchiveCriteria): boolean => {
  if (!matchesDestination(invoice.destination, criteria.destination)) return false;
  if (criteria.year === 'all' && criteria.month === 'all') return true;
  const date = loadDate(invoice);
  if (!date) return false;
  if (criteria.year !== 'all' && date.getFullYear() !== criteria.year) return false;
  if (criteria.month !== 'all' && date.getMonth() !== criteria.month) return false;
  return true;
};

/** Years that have at least one load, newest first. */
export const yearsWithLoads = (invoices: any[]): number[] =>
  Array.from(
    new Set(
      invoices
        .map(invoice => loadDate(invoice)?.getFullYear())
        .filter((year): year is number => typeof year === 'number')
    )
  ).sort((a, b) => b - a);

export const describeCriteria = (criteria: ArchiveCriteria): string => {
  const parts: string[] = [];
  if (criteria.month !== 'all') parts.push(MONTH_NAMES[criteria.month]);
  if (criteria.year !== 'all') parts.push(String(criteria.year));
  if (parts.length === 0) parts.push('All time');
  if (criteria.destination.trim()) parts.push(`"${criteria.destination.trim()}"`);
  return parts.join(' ');
};

const archiveFileName = (criteria: ArchiveCriteria): string => {
  const parts = ['archive'];
  if (criteria.year !== 'all') parts.push(String(criteria.year));
  if (criteria.month !== 'all') parts.push(String(criteria.month + 1).padStart(2, '0'));
  if (criteria.year === 'all' && criteria.month === 'all') parts.push('all-time');
  const destination = criteria.destination.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-');
  if (destination) parts.push(destination.replace(/^-|-$/g, ''));
  return `${parts.join('_')}.xlsx`;
};

// Firestore keeps numbers as numbers; the sheet writer expects the form's strings.
const asText = (value: any): string =>
  value === null || value === undefined ? '' : String(value);

const toInvoiceItem = (invoice: any): InvoiceItem => ({
  ...defaultInvoiceItem,
  ...invoice,
  destination: asText(invoice.destination),
  date: asText(invoice.date),
  bolNumber: asText(invoice.bolNumber),
  orderNumber: asText(invoice.orderNumber),
  loadNumber: asText(invoice.loadNumber),
  invoiceNumber: asText(invoice.invoiceNumber),
  terminal: asText(invoice.terminal),
  broker: asText(invoice.broker),
  gross: asText(invoice.gross),
  net: asText(invoice.net),
  rate: asText(invoice.rate),
  fuelSurchargePercent: asText(invoice.fuelSurchargePercent),
  totalDem: asText(invoice.totalDem),
  driver: asText(invoice.driver),
  notes: asText(invoice.notes)
});

/**
 * Builds the archive workbook for the given loads: the Loads template filled
 * one row per load (oldest first), plus a Status column and the total recorded
 * in the system (which includes any split share the sheet formulas can't see).
 */
export const createArchiveExcel = async (invoices: any[]): Promise<Blob> => {
  const ordered = [...invoices].sort(
    (a, b) => (loadDate(a)?.getTime() || 0) - (loadDate(b)?.getTime() || 0)
  );

  const filled = await createExcelFromTemplate('archive', ordered.map(toInvoiceItem));

  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(await filled.arrayBuffer());
  const worksheet = workbook.getWorksheet(1);
  if (!worksheet) throw new Error('Archive worksheet not found');

  // Match the existing header styling.
  const headerStyle = worksheet.getCell(HEADER_ROW, STATUS_COLUMN - 1);
  [
    [STATUS_COLUMN, 'Status'],
    [RECORDED_TOTAL_COLUMN, 'Recorded Total']
  ].forEach(([column, label]) => {
    const cell = worksheet.getCell(HEADER_ROW, column as number);
    cell.value = label as string;
    cell.font = headerStyle.font;
    cell.alignment = headerStyle.alignment;
    cell.border = headerStyle.border;
    cell.fill = headerStyle.fill;
  });
  worksheet.getColumn(STATUS_COLUMN).width = 14;
  worksheet.getColumn(RECORDED_TOTAL_COLUMN).width = 16;

  ordered.forEach((invoice, index) => {
    const row = FIRST_DATA_ROW + index;
    worksheet.getCell(row, STATUS_COLUMN).value =
      STATUS_TEXT[invoice.status] || asText(invoice.status) || 'Pending';
    const total = Number(invoice.totalCharges);
    const totalCell = worksheet.getCell(row, RECORDED_TOTAL_COLUMN);
    totalCell.value = isNaN(total) ? null : total;
    totalCell.numFmt = '"$"#,##0.00';
  });

  const buffer = await workbook.xlsx.writeBuffer();
  return new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  });
};

/** Filters, builds and downloads the archive. Returns how many loads went in. */
export const downloadArchive = async (invoices: any[], criteria: ArchiveCriteria): Promise<number> => {
  const matching = invoices.filter(invoice => matchesArchiveCriteria(invoice, criteria));
  if (matching.length === 0) return 0;

  const blob = await createArchiveExcel(matching);
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = archiveFileName(criteria);
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
  return matching.length;
};
